import { TableCell, TableRow } from "@mui/material";
import DotLegalTableWithControlsLocalized from "../../../common/components/dotLegalTableWithControlsLocalized/DotLegalTableWithControlsLocalized.tsx";
import React from "react";
import { DotLegalEmptyState, DotLegalLink, DotLegalMultiSelect, DotLegalOverflowMenu, GridSizes, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useCustomInputFieldTab } from "./CustomInputFieldTab.hooks.ts";
import { InputFieldRowTableModel } from "../InputFields.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import Grid from "@mui/material/Grid2";
import EmptyStateSvg from "../../../contract/contractsEmptyState.svg?react";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import InputFieldAddEditDialog from "../inputFieldAddEditDialog/InputFieldAddEditDialog.tsx";
import InputFieldDeleteDialog from "../inputFieldDeleteDialog/InputFieldDeleteDialog.tsx";
import { getEntityTemplateTranslationKeys, TemplateEntityType } from "../../EntityTemplate.types.ts";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface CustomInputFieldTabProps {
    templateEntityType: TemplateEntityType;
}

function CustomInputFieldTab(props: CustomInputFieldTabProps) {
    const urlProvider = useUrlProvider();
    const { translateString } = useTranslation();
    const {
        isLoading,
        hasInputFields,
        inputFields,
        inputFieldToEdit,
        setInputFieldToEdit,
        canEditInputFields,
        getMenuItems,
        onSaved,
        inputFieldForDeletion,
        setInputFieldForDeletion,
        inputTypeFilterOptions,
        entityTemplateFilterOptions,
        searchedInputTypes,
        setSearchedInputTypes,
        searchedEntityTemplates,
        setSearchedEntityTemplates,
    } = useCustomInputFieldTab(props);

    const translationKeys = getEntityTemplateTranslationKeys(props.templateEntityType);

    const headers = Array<ITableHeader<InputFieldRowTableModel>>();
    headers.push({ property: "name", text: translateString("inputField") });
    headers.push({ property: "description", text: translateString("description") });
    headers.push({ property: "typeString", text: translateString("inputType") });
    headers.push({ property: "entityTemplatesString", text: translateString(translationKeys.templates) });

    const getFilterControls = () => {
        const gridSizes: GridSizes = {
            xs: 6,
            sm: 4,
            md: 2,
            lg: 2,
        };
        const noOptionsLabel = translateString("noOptions");

        return (
            <React.Fragment>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        options={inputTypeFilterOptions}
                        chosenOptions={searchedInputTypes}
                        label={translateString("inputType")}
                        noOptionsLabel={noOptionsLabel}
                        onChange={setSearchedInputTypes}
                        noMargin
                    />
                </Grid>
                <Grid size={{ ...gridSizes }}>
                    <DotLegalMultiSelect
                        options={entityTemplateFilterOptions}
                        chosenOptions={searchedEntityTemplates}
                        label={translateString(translationKeys.template)}
                        noOptionsLabel={noOptionsLabel}
                        onChange={setSearchedEntityTemplates}
                        noMargin
                    />
                </Grid>
            </React.Fragment>
        );
    };

    return (
        <>
            <TablePagePaper>
                {hasInputFields ? (
                    <DotLegalTableWithControlsLocalized
                        headers={headers}
                        isLoading={isLoading}
                        internalScroll
                        data={inputFields}
                        emptyText={translateString("noCustomInputFieldMatchesQuery")}
                        defaultOrderBy={"name"}
                        getUserSpecificPageLength={() => 100}
                        clickableRows={canEditInputFields}
                        hasOverflowMenu
                        extraControls={getFilterControls()}
                        renderRow={(row) => (
                            <TableRow key={row.id} onClick={canEditInputFields ? () => setInputFieldToEdit(row.id) : undefined}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell>{row.typeString}</TableCell>
                                <TableCell>
                                    {row.entityTemplates.map((x, index) => (
                                        <React.Fragment key={x.id}>
                                            <DotLegalLink bold linkColor={"primary"} to={urlProvider.getEditEntityTemplate(x.id)} stopPropagation>
                                                {x.name}
                                            </DotLegalLink>
                                            {index < row.entityTemplates.length - 1 && ", "}
                                        </React.Fragment>
                                    ))}
                                </TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<EmptyStateSvg />} text={translateString("noCustomInputFieldText")} />
                )}
            </TablePagePaper>

            {!isNullOrWhitespace(inputFieldToEdit) && (
                <InputFieldAddEditDialog
                    templateEntityType={props.templateEntityType}
                    onDialogClose={() => setInputFieldToEdit(undefined)}
                    onSaved={onSaved}
                    inputFieldIdToEdit={inputFieldToEdit}
                />
            )}

            {inputFieldForDeletion && (
                <InputFieldDeleteDialog
                    inputFieldToDelete={inputFieldForDeletion}
                    onCloseDialog={() => setInputFieldForDeletion(undefined)}
                    onDeleted={onSaved}
                />
            )}
        </>
    );
}

export default CustomInputFieldTab;
